import React, { useCallback, memo } from 'react';
import { ImageIcon, Film, Mic2, Type, Music, FileText, Sparkles, Wand2, Upload, MonitorUp, Video } from 'lucide-react';
import { NodeType } from '@/types';

interface TryActionItem {
  id: string;
  label: string;
  icon: React.ElementType;
  mode?: string;
}

const TRY_ACTIONS: Record<NodeType, TryActionItem[]> = {
  [NodeType.IMAGE_GENERATOR]: [
    { id: 'img2img', label: '图生图', icon: ImageIcon },
    { id: 'upscale', label: '图片高清', icon: MonitorUp },
  ],
  [NodeType.VIDEO_GENERATOR]: [
    { id: 'first_last_frame', label: '首尾帧生成视频', icon: Video },
    { id: 'first_frame', label: '首帧生成视频', icon: Film },
  ],
  [NodeType.AUDIO_GENERATOR]: [
    { id: 'audio_to_video', label: '音频生视频', icon: Film },
  ],
  [NodeType.PROMPT_INPUT]: [
    { id: 'write_content', label: '自己编写内容', icon: FileText },
    { id: 'txt2video', label: '文生视频', icon: Video },
    { id: 'img2prompt', label: '图片反推提示词', icon: ImageIcon },
    { id: 'txt2music', label: '文字生音乐', icon: Music },
  ],
  [NodeType.MEDIA_UPLOAD]: [],
  [NodeType.VIDEO_ANALYZER]: [],
  [NodeType.IMAGE_EDITOR]: [],
  [NodeType.TEXT_NODE]: [],
  [NodeType.SCRIPT_NODE]: [],
  [NodeType.VIDEO_COMPOSE]: [],
  [NodeType.PANORAMA_GENERATOR]: [],
  [NodeType.GROUP]: [],
};

interface TryActionsProps {
  nodeId: string;
  nodeType: NodeType;
  onTryAction?: (actionId: string) => void;
}

const TryActionsInner: React.FC<TryActionsProps> = ({
  nodeId,
  nodeType,
  onTryAction,
}) => {
  const actions = TRY_ACTIONS[nodeType] || [];

  const handleActionClick = useCallback((action: TryActionItem) => {
    if (onTryAction) {
      onTryAction(action.id);
    }
  }, [onTryAction]);

  if (actions.length === 0) return null;

  return (
    <div className="absolute bottom-4 left-4 flex flex-col gap-2">
      <span className="text-[10px] text-slate-500 font-medium">尝试：</span>
      <div className="flex flex-col gap-1.5">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.id}
              onClick={() => handleActionClick(action)}
              className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-white/5 transition-colors group/action"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <Icon size={12} className="text-slate-500 group-hover/action:text-slate-300" />
              <span className="text-[11px] text-slate-400 group-hover/action:text-slate-200 transition-colors">
                {action.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const TryActions = memo(TryActionsInner, (prevProps, nextProps) => {
  return (
    prevProps.nodeId === nextProps.nodeId &&
    prevProps.nodeType === nextProps.nodeType
  );
});
